import { Snack, Snackbar, Spinner, StatusIconProtoProps } from '@nike/eds';
import { useUserContext } from '../../context/user';
import { FC, useEffect, useState } from 'react';
import { PassPolicy } from '../../models/IPassPolicy';
import { VisitorPassGrid } from '../visitor-pass/visitor-pass';
import TAC from '../terms-and-condition/TAC';
import { PassAvailablilityCard } from './pass-availability/pass-availability';
import { Policy } from './types';
import Pass from '../passes/pass_container/pass';
import { usePassPolicies } from '../../context/PassPolicy';
import style from './dashboard.module.css';
import { MyPass } from '../visitor-pass/my-pass/my-pass';
import dayjs from 'dayjs';
import { useSponsorContext } from '../../context/Sponsor';
import { VisitorPass } from '../../models/IVisitorPass';
import { GetEmployeePass } from 'services/pass-service';
import { AppConfig } from 'utils/constants';

export interface Props {
  modalOpen: boolean;
  onDismiss: () => void;
  passToEdit: VisitorPass | null;
}
export const DashboardContent: FC = () => {
  const userInfo = useUserContext();
  const [passAvailability, setPassAvailability] = useState<Policy[]>([]);
  const [employeePass, setEmployeePass] = useState<VisitorPass | null>(null);
  const { sponsor, setTacDetails, tacDetails } = useSponsorContext();

  const passTypesToShow: number[] = [10, 6];
  const { clearContext, passPolicies, isInitialLoading, policyLoadError } = usePassPolicies();

  const [snackBar, setSnackbar] = useState({
    showSnack: false,
    message: '',
    snackStatus: 'success' as StatusIconProtoProps['status'],
  });

  const [passProps, setPassProps] = useState<Props>({
    modalOpen: false,
    onDismiss: () => setPassProps({ ...passProps, modalOpen: false }),
    passToEdit: null,
  });

  const availablePassesToShow = (policies: PassPolicy[]): Policy[] => {
    return (
      policies
        ?.filter(pol => passTypesToShow.includes(pol.passTypeId))
        .map(pol => ({
          passTypeId: pol.passTypeId,
          displayName: pol.displayName,
          shoppingStartDate: pol.shoppingStartDate,
          shoppingEndDate: pol.shoppingEndDate,
          availablePassesCount: pol.passesAllowed - (pol.passesUsed || 0),
          isUnlimited: pol.passesAllowed === 0,
          isVip: pol.passTypeId === 6,
          availableEndDate: pol.availableEndDate,
        }))
        .sort((a, b) =>
          dayjs(a.availableEndDate || a.shoppingStartDate).diff(
            dayjs(b.availableEndDate || b.shoppingStartDate),
          ),
        ) || []
    );
  };

  useEffect(() => {
    if (sponsor && userInfo?.passplayUser?.sponsorId) {
      GetEmployeePass(userInfo?.passplayUser?.sponsorId)
        .then(pass => {
          setEmployeePass(pass.data || null);
        })
        .catch(() => {
          setEmployeePass(null);
        });
    }
  }, [sponsor, userInfo]);

  useEffect(() => {
    setPassAvailability(availablePassesToShow(passPolicies));
  }, [passPolicies, passProps]);

  useEffect(() => {
    if (snackBar.showSnack && snackBar.snackStatus === 'success') {
      clearContext();
    }
  }, [snackBar.showSnack, snackBar.snackStatus]);

  useEffect(() => {
    if (sponsor && sponsor.agreedToTerms === null) {
      setTacDetails({ tacTemplateName: sponsor.tacLocation, enabletac: true });
    } else {
      setTacDetails({ tacTemplateName: '', enabletac: false });
    }
  }, [sponsor]);

  const handleRequestPass = () => {
    setPassProps({ ...passProps, modalOpen: true });
  };

  const shouldShowMyPass = (): boolean => {
    // Hide if employee pass is null, undefined, or empty object
    if (!employeePass || Object.keys(employeePass).length === 0) {
      return false;
    }

    const employeePolicy = passPolicies.find(
      policy => policy.passTypeId === AppConfig.passType.employee,
    );
    if (
      employeePolicy &&
      AppConfig.policyLocationIdGroups.EU.includes(employeePolicy?.policyLocationId)
    ) {
      return false;
    }

    return true;
  };

  return (
    <>
      {(!userInfo?.passplayUser?.sponsorId || isInitialLoading) && (
        <div className={style.dash__loader}>
          <Spinner size="large" id={style.dash__loader_spinner} />
          <h1 className={style.dash__loadertext}>Loading... </h1>
        </div>
      )}

      {!isInitialLoading && policyLoadError && (
        <div>
          <center>
            <h1>Failed to load pass policies. Please try again later.</h1>
          </center>
        </div>
      )}

      {!isInitialLoading && !policyLoadError && userInfo?.passplayUser?.sponsorId && (
        <>
          <PassAvailablilityCard
            passPolicies={passAvailability}
            onRequestPass={handleRequestPass}
            isModelOpen={passProps.modalOpen}
          ></PassAvailablilityCard>
          <Pass {...passProps} setPassSnackbar={setSnackbar} />
          {shouldShowMyPass() && <MyPass employeePass={employeePass} />}
          <VisitorPassGrid
            passPolicies={passPolicies}
            isModelOpen={passProps.modalOpen}
            setSnackBar={setSnackbar}
          />
          {tacDetails.enabletac && <TAC tacTemplateName={tacDetails.tacTemplateName}></TAC>}
        </>
      )}

      <Snackbar>
        {snackBar.showSnack && (
          <Snack
            id="pass-snackbar"
            hideDismiss={true}
            className="txt_uppercase"
            autoDismissDuration={5000}
            status={snackBar.snackStatus}
            onDismiss={() => setSnackbar(prev => ({ ...prev, showSnack: false }))}
          >
            {snackBar.message}
          </Snack>
        )}
      </Snackbar>
    </>
  );
};

// export const Dashboard: FC = () => {
//     const userInfo = useUserContext();
//     return (
//     <PassPoliciesProvider userInfo={userInfo}>
//         <DashboardContent />
//     </PassPoliciesProvider>
//     );
// };
